import { NumberInput, Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  useEffect, useMemo, useState, memo, useCallback, useRef,
} from "react";
import { FILTER_COMBOBOX_PROPS, FILTER_SELECT_CLASSES } from "./filterStyles";

type Props = {
  handleChange: (
    operator: string,
    value: any,
    type: string,
    entityType: string,
  ) => void;
  fieldSelect: boolean;
  op?: string;
  value?: any;
};

// Move constants outside component
const OPS = [
  { label: "is equal to", value: "eq" },
  { label: "is not equal to", value: "ne" },
  { label: "is greater than", value: "gt" },
  { label: "is less than", value: "lt" },
  { label: "is known", value: "known" },
  { label: "is unknown", value: "unknown" },
];

const NumberFilter = memo((props: Props) => {
  const {
    op,
    value,
    handleChange,
    fieldSelect,
  } = props;

  // Normalize value to a number for NumberInput compatibility
  const normalizeValue = (v: any): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return parseInt(v, 10) || 0;
    if (v && typeof v === 'object' && 'start' in v) return v.start ?? 0;
    if (Array.isArray(v)) return Number(v[0]) || 0;
    return 0;
  };

  // Only local state for input value (for debouncing)
  const [inputValue, setInputValue] = useState<any>(normalizeValue(value));
  const [debouncedVal] = useDebouncedValue(inputValue, 700);
  const prevDebouncedValRef = useRef<any>(debouncedVal);
  const isUserInputRef = useRef(false);

  // Sync input value with prop value (only if not from user input)
  useEffect(() => {
    if (!isUserInputRef.current && value !== undefined && value !== null) {
      const normalized = normalizeValue(value);
      if (normalized !== inputValue) {
        setInputValue(normalized);
      }
    }
    isUserInputRef.current = false; // Reset flag
  }, [value, inputValue]);

  // Derive current operator from props (fully controlled)
  // Map gte to gt and lte to lt for display purposes
  const selectOp = useMemo(
    () => {
      if (!op) return null;
      // Map gte -> gt, lte -> lt for UI display
      const mappedOp = op === "gte" ? "gt" : (op === "lte" ? "lt" : op);
      return mappedOp;
    },
    [op]
  );

  // Generate stable ID
  const inputId = useMemo(() => `number-filter-${op || 'empty'}`, [op]);

  // Call handleChange on operator change
  // Preserve original operator (gte/lte) if it was gte/lte, otherwise use the selected operator
  const handleOpChange = useCallback((newOp: string | null) => {
    if (!newOp) return;
    
    if (
      newOp === "known"
      || newOp === "unknown"
      || ((!!debouncedVal || debouncedVal === 0) && !!newOp)
    ) {
      // If the original op was gte/lte, preserve it; otherwise use the new operator
      const operatorToUse = (op === "gte" || op === "lte") ? op : newOp;
      handleChange(
        operatorToUse,
        parseInt(debouncedVal, 10),
        "int",
        "column",
      );
    }
  }, [debouncedVal, handleChange, op]);

  // Call handleChange when debounced value changes (only if user typed)
  useEffect(() => {
    // Only call if debounced value actually changed from user input
    if (prevDebouncedValRef.current === debouncedVal) return;
    
    const operator = selectOp || "";
    if (
      operator &&
      operator !== "known" &&
      operator !== "unknown" &&
      (!!debouncedVal || debouncedVal === 0)
    ) {
      prevDebouncedValRef.current = debouncedVal;
      // If the original op was gte/lte, preserve it; otherwise use the mapped operator
      const operatorToUse = (op === "gte" || op === "lte") ? op : operator;
      handleChange(
        operatorToUse,
        parseInt(debouncedVal, 10),
        "int",
        "column",
      );
    }
  }, [debouncedVal, selectOp, op, handleChange]);

  const handleValueChange = useCallback((val: number | string) => {
    isUserInputRef.current = true; // Mark as user input
    setInputValue(val);
  }, []);

  return (
    <>
      <Select
        disabled={fieldSelect}
        className="min-w-[170px]"
        classNames={FILTER_SELECT_CLASSES}
        comboboxProps={FILTER_COMBOBOX_PROPS}
        placeholder="Select Condition"
        data={OPS}
        value={selectOp}
        onChange={handleOpChange}
        size="xs"
      />
      {selectOp && selectOp !== "known" && selectOp !== "unknown" && (
        <NumberInput
          disabled={!selectOp}
          min={0}
          placeholder="Enter number"
          className="border-0"
          id={inputId}
          classNames={{
            input: "bg-background text-foreground border-border",
          }}
          value={inputValue}
          onChange={handleValueChange}
          size="xs"
        />
      )}
    </>
  );
});

NumberFilter.displayName = "NumberFilter";

export default NumberFilter;
